import React, { useEffect, useState, useCallback } from 'react';
import { SpaceBetween, Box, Button, Spinner, Badge, Cards, Input, Select, ColumnLayout, Header } from '@cloudscape-design/components';
import { browse } from './browseClient';
import { imgBase } from '../config';
import AgentRevealBanner from './AgentRevealBanner';

function TitleDetail({ id, onBack, onOpenPerson }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    let alive = true;
    browse.marquee({ title: id }).then((d) => alive && setT(d)).catch(() => alive && setT({ error: true }));
    return () => { alive = false; };
  }, [id]);
  if (!t) return <Spinner />;
  if (t.error) return <Box color="text-status-error">Title not found.</Box>;
  const cast = t.castResolved || [];
  const directors = t.directors || [];
  return (
    <SpaceBetween size="m">
      <Button iconName="angle-left" variant="link" onClick={onBack}>Back to The Marquee</Button>
      <ColumnLayout columns={2}>
        <Box textAlign="center">
          <img
            src={`${imgBase()}/img/marquee/${t.id}.jpg`}
            alt=""
            onError={(e) => { e.currentTarget.outerHTML = '<div style="font-size:96px">🎬</div>'; }}
            style={{ width: '100%', maxWidth: 220, aspectRatio: '2/3', objectFit: 'cover', borderRadius: 8, border: '1px solid #3a332c' }}
          />
        </Box>
        <SpaceBetween size="xs">
          <Box variant="h1">{t.title}</Box>
          <Box color="text-body-secondary">{t.year}{t.type ? ` · ${t.type}` : ''}{t.runtimeMinutes ? ` · ${t.runtimeMinutes}m` : ''}</Box>
          <Box>⭐ {t.rating}{t.voteCount ? ` · ${t.voteCount.toLocaleString()} votes` : ''}</Box>
          <SpaceBetween direction="horizontal" size="xxs">
            {(t.genres || []).map((g) => <Badge key={g}>{g}</Badge>)}
          </SpaceBetween>
          {t.synopsis && <Box>{t.synopsis}</Box>}
          {directors.length > 0 && (
            <Box fontSize="body-s">
              <b>{directors.length > 1 ? 'Directors' : 'Director'}:</b>{' '}
              {directors.map((d, i) => (
                <React.Fragment key={d.personId}>
                  {i > 0 && ', '}
                  <Button variant="inline-link" onClick={() => onOpenPerson(d.personId)}>{d.name}</Button>
                </React.Fragment>
              ))}
            </Box>
          )}
        </SpaceBetween>
      </ColumnLayout>

      {cast.length > 0 && (
        <>
          <Header variant="h3">Top cast</Header>
          <ColumnLayout columns={2}>
            {cast.map((c) => (
              <div key={c.personId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, borderBottom: '1px solid #f0f1f3', paddingBottom: 4 }}>
                <Button variant="inline-link" onClick={() => onOpenPerson(c.personId)}>{c.name}</Button>
                <Box fontSize="body-s" color="text-body-secondary">{c.character}</Box>
              </div>
            ))}
          </ColumnLayout>
        </>
      )}

      {(t.reviews || []).length > 0 && (
        <>
          <Header variant="h3">Reviews</Header>
          <SpaceBetween size="xs">
            {t.reviews.slice(0, 3).map((r, i) => (
              <Box key={i}>
                <Box fontSize="body-s"><b>{r.outlet || r.author || 'Critic'}</b> {r.stars ? `· ⭐ ${r.stars}` : ''}</Box>
                <Box fontSize="body-s" color="text-body-secondary">{r.body || r.text}</Box>
              </Box>
            ))}
          </SpaceBetween>
        </>
      )}
    </SpaceBetween>
  );
}

function PersonDetail({ id, onBack, onOpenTitle }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    let alive = true;
    browse.person(id).then((d) => alive && setP(d)).catch(() => alive && setP({ error: true }));
    return () => { alive = false; };
  }, [id]);
  if (!p) return <Spinner />;
  if (p.error) return <Box color="text-status-error">Person not found.</Box>;
  return (
    <SpaceBetween size="m">
      <Button iconName="angle-left" variant="link" onClick={onBack}>Back</Button>
      <Box variant="h1">{p.name}</Box>
      <Box color="text-body-secondary">
        {(p.roles || []).join(' · ')}{p.bornYear ? ` · b. ${p.bornYear}` : ''}
      </Box>
      {p.bio && <Box>{p.bio}</Box>}
      {(p.knownFor || []).length > 0 && (
        <>
          <Header variant="h3">Known for</Header>
          <SpaceBetween size="xxs">
            {p.knownFor.map((k) => (
              <Button key={k.id} variant="inline-link" onClick={() => onOpenTitle(k.id)}>
                {k.title}{k.year ? ` (${k.year})` : ''}
              </Button>
            ))}
          </SpaceBetween>
        </>
      )}
    </SpaceBetween>
  );
}

export default function MarqueeSite({ reveals = [] }) {
  const [q, setQ] = useState('');
  const [genre, setGenre] = useState({ label: 'All genres', value: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openPerson, setOpenPerson] = useState(null);

  const load = useCallback((query, g) => {
    setLoading(true);
    const params = {};
    if (query) params.q = query;
    if (g) params.genre = g;
    browse.marquee(params)
      .then(setData)
      .catch(() => setData({ titles: [], genres: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load('', ''); }, [load]);

  if (openPerson) return <PersonDetail id={openPerson} onBack={() => setOpenPerson(null)} onOpenTitle={(tid) => { setOpenPerson(null); setOpenId(tid); }} />;
  if (openId) return <TitleDetail id={openId} onBack={() => setOpenId(null)} onOpenPerson={(pid) => setOpenPerson(pid)} />;

  const genreOptions = [{ label: 'All genres', value: '' }]
    .concat((data?.genres || []).map((g) => ({ label: g, value: g })));

  return (
    <SpaceBetween size="m">
      {/* Dark cinema masthead */}
      <div style={{
        background: 'linear-gradient(180deg, #1b1713, #12100d)', borderRadius: 8,
        padding: '14px 16px', color: '#e8dcc4',
      }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 700, color: '#caa34a' }}>
          🎬 {data?.name || 'The Marquee'}
        </div>
        <div style={{ fontStyle: 'italic', fontSize: 12, color: '#a89a82' }}>
          {data?.tagline || 'Every title, every credit — pay per lookup.'}
        </div>
      </div>

      <AgentRevealBanner reveals={reveals} accent="#caa34a" />
      <SpaceBetween direction="horizontal" size="xs">
        <div style={{ flex: 1, minWidth: 200 }}>
          <Input
            value={q}
            type="search"
            placeholder="Search titles…"
            onChange={({ detail }) => setQ(detail.value)}
            onKeyDown={({ detail }) => detail.key === 'Enter' && load(q, genre.value)}
          />
        </div>
        <Select
          selectedOption={genre}
          options={genreOptions}
          onChange={({ detail }) => { setGenre(detail.selectedOption); load(q, detail.selectedOption.value); }}
        />
        <Button onClick={() => load(q, genre.value)}>Search</Button>
      </SpaceBetween>

      {loading ? (
        <Spinner />
      ) : (
        <Cards
          cardDefinition={{
            header: (t) => (
              <Button variant="inline-link" onClick={() => setOpenId(t.id)}>
                {t.emoji || '🎬'} {t.title}
              </Button>
            ),
            sections: [
              { id: 'poster', content: (t) => (
                <button onClick={() => setOpenId(t.id)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', width: '100%' }}>
                  <img
                    src={`${imgBase()}/img/marquee/${t.id}.jpg`}
                    alt=""
                    onError={(e) => { e.currentTarget.outerHTML = '<div style="aspect-ratio:2/3;display:flex;align-items:center;justify-content:center;font-size:48px;background:#15110e;border-radius:6px">🎬</div>'; }}
                    style={{ width: '100%', aspectRatio: '2/3', objectFit: 'cover', borderRadius: 6 }}
                  />
                </button>
              ) },
              { id: 'meta', content: (t) => (
                <SpaceBetween size="xxs">
                  <Box fontSize="body-s" color="text-body-secondary">{t.year}{t.kind ? ` · ${t.kind}` : ''} · ⭐ {t.rating}</Box>
                  <SpaceBetween direction="horizontal" size="xxs">
                    {(t.genres || []).slice(0, 3).map((g) => <Badge key={g}>{g}</Badge>)}
                  </SpaceBetween>
                </SpaceBetween>
              ) },
            ],
          }}
          items={data?.titles || []}
          cardsPerRow={[{ cards: 2 }, { minWidth: 500, cards: 3 }]}
          trackBy="id"
          empty={<Box textAlign="center" color="text-body-secondary">No titles found.</Box>}
        />
      )}
    </SpaceBetween>
  );
}
